/**
 * ============================================================================
 * USER / TENANT PAGE: Report Management Suite
 * ============================================================================
 * Purpose:
 *   Comprehensive analytics and delivery reports covering:
 *   1. Campaign Report: In-depth delivery metrics & recipient logs for broadcast campaigns.
 *   2. General Report: Message transmission logs across all UI-driven broadcasts.
 *   3. API Report: Delivery tracking for programmatic API-dispatched messages.
 * ============================================================================
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { campaignService } from "../../services";
import { getCurrentUser, isSuperAdmin, isAdmin } from "../../../core/utils/auth";

// --- SVG Icons ---
const ChartBarIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z" />
  </svg>
);
const MegaphoneIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 1 1 0-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18c.253.962.584 1.892.985 2.783.247.55.06 1.21-.463 1.511l-.657.38c-.551.318-1.26.117-1.527-.455a20.443 20.443 0 0 1-1.378-3.957m3.04-9.442c.253-.962.584-1.892.985-2.783.247-.55.06-1.21-.463-1.511l-.657-.38c-.551-.318-1.26-.117-1.527.455a20.443 20.443 0 0 0-1.378 3.957m15.69 4.316a11.96 11.96 0 0 1-5.187 4.225m0-8.45A11.96 11.96 0 0 1 21.05 12" />
  </svg>
);
const CommandLineIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 7.5l3 3-3 3m4.5 0h6m-13.5 6h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5z" />
  </svg>
);
const DocumentTextIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
  </svg>
);
const MagnifyingGlassIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
  </svg>
);
const ArrowDownTrayIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
  </svg>
);
const ArrowPathIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
  </svg>
);
const CalendarIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 9v7.5" />
  </svg>
);
const ChevronLeftIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
  </svg>
);
const ChevronRightIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
  </svg>
);

const XMarkIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);
const ExclamationTriangleIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
  </svg>
);
const ClipboardDocumentIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.842A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.592m7.332 0c.055.194.084.4.084.608v1.05a2.25 2.25 0 0 1-2.25 2.25h-3a2.25 2.25 0 0 1-2.25-2.25V4.45c0-.208.03-.414.085-.608m7.332 0l-.82 11.48A2.25 2.25 0 0 1 12.83 17.25H6.17a2.25 2.25 0 0 1-2.246-1.928L3.104 3.842" />
  </svg>
);
const PhoneIcon = ({ className = "w-3 h-3" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z" />
  </svg>
);

const STATUS_PILL_STYLES = {
  DELIVERED: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border-emerald-200",
  READ: "bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300 border-blue-200",
  SENT: "bg-teal-100 text-teal-800 dark:bg-teal-950/60 dark:text-teal-300 border-teal-200",
  FAILED: "bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300 border-rose-200",
  QUEUED: "bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 border-amber-200",
  PENDING: "bg-orange-100 text-orange-800 dark:bg-orange-950/60 dark:text-orange-300 border-orange-200",
  DRAFT: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300 border-gray-300",
};

export default function ReportManagement({ defaultTab }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const currentUser = useMemo(() => getCurrentUser(), []);
  const showCredits = useMemo(() => {
    if (!currentUser) return true;
    if (isSuperAdmin(currentUser) || isAdmin(currentUser)) return true;
    return currentUser.bool_show_credits !== false;
  }, [currentUser]);

  // Tab State: "campaigns" | "general" | "api"
  const activeTab = useMemo(() => {
    return defaultTab || searchParams.get("tab") || "campaigns";
  }, [defaultTab, searchParams]);

  const setTab = (tab) => {
    const params = new URLSearchParams(searchParams);
    params.set("tab", tab);
    setSearchParams(params);
  };

  // ──────────────────────────────────────────────────────────────────────────
  // 1. CAMPAIGN REPORT STATE
  // ──────────────────────────────────────────────────────────────────────────
  const [campaignsList, setCampaignsList] = useState([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState(
    searchParams.get("campaignId") || searchParams.get("id") || ""
  );
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignMessages, setCampaignMessages] = useState([]);
  const [loadingCampaignData, setLoadingCampaignData] = useState(false);
  const [campaignSearch, setCampaignSearch] = useState("");
  const [campaignStatusFilter, setCampaignStatusFilter] = useState("all");
  const [campaignSenderFilter, setCampaignSenderFilter] = useState("all");

  // Unique available sender numbers for the current campaign
  const availableCampaignSenders = useMemo(() => {
    const set = new Set();
    if (Array.isArray(selectedCampaign?.arr_sender_numbers)) {
      selectedCampaign.arr_sender_numbers.forEach((n) => {
        if (n) set.add(String(n).replace(/^\+/, "").trim());
      });
    }
    if (selectedCampaign?.str_sender_number) {
      set.add(String(selectedCampaign.str_sender_number).replace(/^\+/, "").trim());
    }
    if (selectedCampaign?.str_phone_number_id) {
      set.add(String(selectedCampaign.str_phone_number_id).replace(/^\+/, "").trim());
    }
    (campaignMessages || []).forEach((m) => {
      const s = m.str_sender_number || m.str_phone_number_id;
      if (s) set.add(String(s).replace(/^\+/, "").trim());
    });
    return Array.from(set);
  }, [selectedCampaign, campaignMessages]);

  // Client-side instant filter fallback for sender numbers
  const displayedMessages = useMemo(() => {
    if (!campaignSenderFilter || campaignSenderFilter === "all") return campaignMessages;
    const cleanFilter = String(campaignSenderFilter).replace(/^\+/, "").trim();
    return campaignMessages.filter((m) => {
      const s = String(m.str_sender_number || m.str_phone_number_id || "").replace(/^\+/, "").trim();
      return s === cleanFilter;
    });
  }, [campaignMessages, campaignSenderFilter]);

  // Load All Campaigns for dropdown selection
  const loadCampaignsDropdown = useCallback(async () => {
    try {
      const res = await campaignService.getCampaigns({ limit: 100 });
      const list = res?.data || (Array.isArray(res) ? res : []);
      setCampaignsList(list);

      // If URL has campaignId, find it; otherwise default to first campaign
      const targetId = searchParams.get("campaignId") || searchParams.get("id");
      if (targetId) {
        setSelectedCampaignId(String(targetId));
      } else if (list.length > 0 && !selectedCampaignId) {
        setSelectedCampaignId(String(list[0].int_campaign_id || list[0]._id));
      }
    } catch (err) {
      console.error("Failed to load campaigns list for reports:", err);
    }
  }, [searchParams, selectedCampaignId]);

  useEffect(() => {
    loadCampaignsDropdown();
  }, [loadCampaignsDropdown]);

  // Load Details & Messages for Selected Campaign
  const loadSelectedCampaignReport = useCallback(async () => {
    if (!selectedCampaignId) return;
    setLoadingCampaignData(true);
    try {
      // Find campaign object
      const camp = campaignsList.find(
        (c) =>
          String(c.int_campaign_id) === String(selectedCampaignId) ||
          String(c._id) === String(selectedCampaignId) ||
          String(c.str_campaign_id) === String(selectedCampaignId)
      );
      if (camp) setSelectedCampaign(camp);

      // Fetch campaign message logs
      const msgRes = await campaignService.getCampaignMessages(selectedCampaignId, {
        limit: 200,
        search: campaignSearch || undefined,
        status: campaignStatusFilter !== "all" ? campaignStatusFilter : undefined,
        senderNumber: campaignSenderFilter !== "all" ? campaignSenderFilter : undefined,
      });

      if (msgRes && msgRes.success) {
        setCampaignMessages(msgRes.data || []);
        if (msgRes.campaign) {
          setSelectedCampaign((prev) => ({ ...(prev || {}), ...msgRes.campaign }));
        }
      } else if (Array.isArray(msgRes)) {
        setCampaignMessages(msgRes);
      } else {
        setCampaignMessages([]);
      }
    } catch (err) {
      console.error("Failed to load campaign messages:", err);
      setCampaignMessages([]);
    } finally {
      setLoadingCampaignData(false);
    }
  }, [selectedCampaignId, campaignsList, campaignSearch, campaignStatusFilter, campaignSenderFilter]);

  useEffect(() => {
    if (activeTab === "campaigns" && selectedCampaignId) {
      loadSelectedCampaignReport();
    }
  }, [activeTab, selectedCampaignId, loadSelectedCampaignReport]);

  // Handle Dropdown Change
  const handleCampaignSelect = (id) => {
    setSelectedCampaignId(id);
    setCampaignSenderFilter("all");
    const params = new URLSearchParams(searchParams);
    params.set("campaignId", id);
    setSearchParams(params);
  };

  // ──────────────────────────────────────────────────────────────────────────
  // 2. GENERAL REPORT STATE (tbl_general_campaign_details)
  // ──────────────────────────────────────────────────────────────────────────
  const formatDateToYMD = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getTodayDateStr = () => formatDateToYMD(new Date());

  const getYesterdayDateStr = () => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return formatDateToYMD(d);
  };

  const get7DaysAgoDateStr = () => {
    const d = new Date();
    d.setDate(d.getDate() - 6);
    return formatDateToYMD(d);
  };

  const getThisMonthStartDateStr = () => {
    const d = new Date();
    d.setDate(1);
    return formatDateToYMD(d);
  };

  // Date Range state
  const [generalStartDate, setGeneralStartDate] = useState(getTodayDateStr());
  const [generalEndDate, setGeneralEndDate] = useState(getTodayDateStr());
  const [generalDatePreset, setGeneralDatePreset] = useState("today"); // "today" | "yesterday" | "7days" | "thisMonth" | "all" | "custom"

  // Data & Stats
  const [generalDetails, setGeneralDetails] = useState([]);
  const [generalStats, setGeneralStats] = useState({
    total: 0,
    sent: 0,
    delivered: 0,
    read: 0,
    failed: 0,
    queued: 0,
    pending: 0,
    total_cost: 0,
  });
  const [loadingGeneral, setLoadingGeneral] = useState(false);
  const [generalSearch, setGeneralSearch] = useState("");
  const [generalStatusFilter, setGeneralStatusFilter] = useState("all");
  const [selectedGeneralError, setSelectedGeneralError] = useState(null);

  // Pagination state
  const [generalPage, setGeneralPage] = useState(1);
  const [generalLimit, setGeneralLimit] = useState(20);
  const [generalPagination, setGeneralPagination] = useState({
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 1,
  });

  const loadGeneralReport = useCallback(async () => {
    setLoadingGeneral(true);
    try {
      const params = {
        page: generalPage,
        limit: generalLimit,
        search: generalSearch || undefined,
        status: generalStatusFilter !== "all" ? generalStatusFilter : undefined,
      };

      if (generalDatePreset === "all") {
        params.date = "all";
      } else {
        if (generalStartDate) params.startDate = generalStartDate;
        if (generalEndDate) params.endDate = generalEndDate;
        if (!generalStartDate && !generalEndDate) {
          params.date = getTodayDateStr();
        }
      }

      const res = await campaignService.getGeneralDetails(params);
      const data = res?.data || (Array.isArray(res) ? res : []);
      setGeneralDetails(data);

      if (res?.pagination) {
        setGeneralPagination({
          total: res.pagination.total ?? data.length,
          page: res.pagination.page ?? generalPage,
          limit: res.pagination.limit ?? generalLimit,
          totalPages: res.pagination.totalPages ?? (Math.ceil((res.pagination.total || data.length) / generalLimit) || 1),
        });
      } else {
        setGeneralPagination({
          total: data.length,
          page: generalPage,
          limit: generalLimit,
          totalPages: 1,
        });
      }

      if (res?.stats) {
        setGeneralStats(res.stats);
      } else {
        const stats = {
          total: data.length,
          sent: 0,
          delivered: 0,
          read: 0,
          failed: 0,
          queued: 0,
          pending: 0,
          total_cost: 0,
        };
        data.forEach((item) => {
          const st = (item.str_status || "").toUpperCase();
          if (st === "SENT" || st === "SEND") stats.sent++;
          else if (st === "DELIVERED") stats.delivered++;
          else if (st === "READ") stats.read++;
          else if (st === "FAILED") stats.failed++;
          else if (st === "QUEUED") stats.queued++;
          else if (st === "PENDING") stats.pending++;
          if (item.dbl_cost) stats.total_cost += Number(item.dbl_cost);
        });
        stats.total_cost = Number(stats.total_cost.toFixed(4));
        setGeneralStats(stats);
      }
    } catch (err) {
      console.error("Failed to load general details:", err);
      setGeneralDetails([]);
      setGeneralPagination({ total: 0, page: 1, limit: generalLimit, totalPages: 1 });
      setGeneralStats({
        total: 0,
        sent: 0,
        delivered: 0,
        read: 0,
        failed: 0,
        queued: 0,
        pending: 0,
        total_cost: 0,
      });
    } finally {
      setLoadingGeneral(false);
    }
  }, [generalStartDate, generalEndDate, generalDatePreset, generalSearch, generalStatusFilter, generalPage, generalLimit]);

  useEffect(() => {
    if (activeTab === "general") {
      loadGeneralReport();
    }
  }, [activeTab, loadGeneralReport]);

  // Date range presets handler
  const handleDatePreset = (preset) => {
    setGeneralDatePreset(preset);
    setGeneralPage(1);
    if (preset === "today") {
      const today = getTodayDateStr();
      setGeneralStartDate(today);
      setGeneralEndDate(today);
    } else if (preset === "yesterday") {
      const yest = getYesterdayDateStr();
      setGeneralStartDate(yest);
      setGeneralEndDate(yest);
    } else if (preset === "7days") {
      setGeneralStartDate(get7DaysAgoDateStr());
      setGeneralEndDate(getTodayDateStr());
    } else if (preset === "thisMonth") {
      setGeneralStartDate(getThisMonthStartDateStr());
      setGeneralEndDate(getTodayDateStr());
    } else if (preset === "all") {
      setGeneralStartDate("");
      setGeneralEndDate("");
    }
  };

  const handleStartDateChange = (val) => {
    setGeneralDatePreset("custom");
    setGeneralPage(1);
    setGeneralStartDate(val);
  };

  const handleEndDateChange = (val) => {
    setGeneralDatePreset("custom");
    setGeneralPage(1);
    setGeneralEndDate(val);
  };

  const handleGeneralSearchChange = (val) => {
    setGeneralSearch(val);
    setGeneralPage(1);
  };

  const handleGeneralStatusChange = (val) => {
    setGeneralStatusFilter(val);
    setGeneralPage(1);
  };

  const getGeneralDateRangeLabel = () => {
    if (generalDatePreset === "all") return "All Dates";
    if (generalDatePreset === "today" || (generalStartDate === getTodayDateStr() && generalEndDate === getTodayDateStr())) {
      return `Today (${getTodayDateStr()})`;
    }
    if (generalDatePreset === "yesterday" || (generalStartDate === getYesterdayDateStr() && generalEndDate === getYesterdayDateStr())) {
      return `Yesterday (${getYesterdayDateStr()})`;
    }
    if (generalDatePreset === "7days") {
      return `Last 7 Days (${generalStartDate} to ${generalEndDate})`;
    }
    if (generalDatePreset === "thisMonth") {
      return `This Month (${generalStartDate} to ${generalEndDate})`;
    }
    if (generalStartDate && generalEndDate) {
      if (generalStartDate === generalEndDate) return generalStartDate;
      return `${generalStartDate} to ${generalEndDate}`;
    }
    if (generalStartDate) return `From ${generalStartDate}`;
    if (generalEndDate) return `Up to ${generalEndDate}`;
    return "All Dates";
  };

  // Generate numbered pages for pagination
  const generalPageNumbers = useMemo(() => {
    const totalPages = generalPagination.totalPages || 1;
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const current = generalPage;
    if (current <= 4) {
      return [1, 2, 3, 4, 5, "...", totalPages];
    }
    if (current >= totalPages - 3) {
      return [1, "...", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    }
    return [1, "...", current - 1, current, current + 1, "...", totalPages];
  }, [generalPage, generalPagination.totalPages]);

  // Full dataset CSV Export for General Report
  const handleExportGeneralCsv = async () => {
    if (generalPagination.total === 0) return;
    try {
      let rowsToExport = generalDetails;
      if (generalPagination.total > generalDetails.length) {
        const res = await campaignService.getGeneralDetails({
          page: 1,
          limit: 10000,
          search: generalSearch || undefined,
          status: generalStatusFilter !== "all" ? generalStatusFilter : undefined,
          ...(generalDatePreset === "all"
            ? { date: "all" }
            : {
                startDate: generalStartDate || undefined,
                endDate: generalEndDate || undefined,
              }),
        });
        if (res?.data && res.data.length > 0) {
          rowsToExport = res.data;
        }
      }

      const exportColumns = [
        { label: "Recipient", key: (r) => `+${r.str_recipient_number}` },
        { label: "Template", key: "str_template_name" },
        { label: "Status", key: "str_status" },
        ...(showCredits ? [{ label: "Cost (₹)", key: (r) => (r.dbl_cost != null ? r.dbl_cost : 0) }] : []),
        { label: "Msg ID", key: "str_operator_message_id" },
        {
          label: "Sent At",
          key: (r) =>
            r.dttm_sent_on
              ? new Date(r.dttm_sent_on).toLocaleString()
              : r.dttm_created_on
              ? new Date(r.dttm_created_on).toLocaleString()
              : "",
        },
        {
          label: "Delivered At",
          key: (r) => (r.dttm_delivered_on ? new Date(r.dttm_delivered_on).toLocaleString() : ""),
        },
        {
          label: "Read At",
          key: (r) => (r.dttm_read_on ? new Date(r.dttm_read_on).toLocaleString() : ""),
        },
        { label: "Error Code", key: (r) => r.str_error_code || "" },
        { label: "Error Message", key: (r) => r.str_error_message || "" },
      ];

      exportToCsv(
        `General_Messages_${generalStartDate || "All"}_to_${generalEndDate || "All"}`,
        rowsToExport,
        exportColumns
      );
    } catch (e) {
      console.error("Export error:", e);
      const fallbackColumns = [
        { label: "Recipient", key: (r) => `+${r.str_recipient_number}` },
        { label: "Template", key: "str_template_name" },
        { label: "Status", key: "str_status" },
        ...(showCredits ? [{ label: "Cost (₹)", key: (r) => (r.dbl_cost != null ? r.dbl_cost : 0) }] : []),
        { label: "Msg ID", key: "str_operator_message_id" },
        {
          label: "Sent At",
          key: (r) =>
            r.dttm_sent_on
              ? new Date(r.dttm_sent_on).toLocaleString()
              : r.dttm_created_on
              ? new Date(r.dttm_created_on).toLocaleString()
              : "",
        },
        {
          label: "Delivered At",
          key: (r) => (r.dttm_delivered_on ? new Date(r.dttm_delivered_on).toLocaleString() : ""),
        },
        {
          label: "Read At",
          key: (r) => (r.dttm_read_on ? new Date(r.dttm_read_on).toLocaleString() : ""),
        },
        { label: "Error Code", key: (r) => r.str_error_code || "" },
        { label: "Error Message", key: (r) => r.str_error_message || "" },
      ];
      exportToCsv(
        `General_Messages_${generalStartDate || "All"}`,
        generalDetails,
        fallbackColumns
      );
    }
  };

  // ──────────────────────────────────────────────────────────────────────────
  // 3. API REPORT STATE (tbl_api_campaign_details)
  // ──────────────────────────────────────────────────────────────────────────
  const [apiDetails, setApiDetails] = useState([]);
  const [loadingApi, setLoadingApi] = useState(false);
  const [apiSearch, setApiSearch] = useState("");
  const [apiStatusFilter, setApiStatusFilter] = useState("all");
  const [apiPage, setApiPage] = useState(1);
  const [apiLimit, setApiLimit] = useState(20);
  const [apiPagination, setApiPagination] = useState({
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 1,
  });
  const [apiStats, setApiStats] = useState({
    total: 0,
    sent: 0,
    delivered: 0,
    read: 0,
    failed: 0,
    queued: 0,
    total_cost: 0,
  });
  const [apiDatePreset, setApiDatePreset] = useState("all");
  const [apiStartDate, setApiStartDate] = useState("");
  const [apiEndDate, setApiEndDate] = useState("");
  const [selectedApiError, setSelectedApiError] = useState(null);

  const loadApiReport = useCallback(async () => {
    setLoadingApi(true);
    try {
      const params = {
        page: apiPage,
        limit: apiLimit,
        search: apiSearch || undefined,
        status: apiStatusFilter !== "all" ? apiStatusFilter : undefined,
      };

      if (apiDatePreset === "all") {
        params.date = "all";
      } else if (apiDatePreset === "today") {
        params.date = getTodayDateStr();
      } else if (apiDatePreset === "yesterday") {
        const y = new Date();
        y.setDate(y.getDate() - 1);
        params.date = y.toISOString().slice(0, 10);
      } else if (apiDatePreset === "7days") {
        const d = new Date();
        d.setDate(d.getDate() - 7);
        params.startDate = d.toISOString().slice(0, 10);
        params.endDate = getTodayDateStr();
      } else if (apiDatePreset === "thisMonth") {
        const now = new Date();
        params.startDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
        params.endDate = getTodayDateStr();
      } else {
        if (apiStartDate) params.startDate = apiStartDate;
        if (apiEndDate) params.endDate = apiEndDate;
      }

      const res = await campaignService.getApiDetails(params);
      const data = res?.data || (Array.isArray(res) ? res : []);
      setApiDetails(data);

      if (res?.pagination) {
        setApiPagination({
          total: res.pagination.total ?? data.length,
          page: res.pagination.page ?? apiPage,
          limit: res.pagination.limit ?? apiLimit,
          totalPages: res.pagination.totalPages ?? (Math.ceil((res.pagination.total || data.length) / apiLimit) || 1),
        });
      } else {
        setApiPagination({
          total: data.length,
          page: apiPage,
          limit: apiLimit,
          totalPages: Math.ceil(data.length / apiLimit) || 1,
        });
      }

      if (res?.stats) {
        setApiStats(res.stats);
      } else {
        const stats = {
          total: data.length,
          sent: 0,
          delivered: 0,
          read: 0,
          failed: 0,
          queued: 0,
          total_cost: 0,
        };
        data.forEach((item) => {
          const st = (item.str_status || "").toUpperCase();
          if (st === "SENT" || st === "SEND") stats.sent++;
          else if (st === "DELIVERED") stats.delivered++;
          else if (st === "READ") stats.read++;
          else if (st === "FAILED") stats.failed++;
          else if (st === "QUEUED" || st === "PENDING") stats.queued++;
          if (item.dbl_cost) stats.total_cost += Number(item.dbl_cost);
        });
        stats.total_cost = Number(stats.total_cost.toFixed(4));
        setApiStats(stats);
      }
    } catch (err) {
      console.error("Failed to load API details:", err);
      setApiDetails([]);
      setApiPagination({ total: 0, page: 1, limit: apiLimit, totalPages: 1 });
      setApiStats({ total: 0, sent: 0, delivered: 0, read: 0, failed: 0, queued: 0, total_cost: 0 });
    } finally {
      setLoadingApi(false);
    }
  }, [apiPage, apiLimit, apiSearch, apiStatusFilter, apiDatePreset, apiStartDate, apiEndDate]);

  useEffect(() => {
    if (activeTab === "api") {
      loadApiReport();
    }
  }, [activeTab, loadApiReport]);

  const handleApiDatePreset = (preset) => {
    setApiDatePreset(preset);
    setApiStartDate("");
    setApiEndDate("");
    setApiPage(1);
  };

  const handleApiCustomDateApply = (e) => {
    e.preventDefault();
    if (apiStartDate || apiEndDate) {
      setApiDatePreset("custom");
      setApiPage(1);
    }
  };

  const apiPageNumbers = useMemo(() => {
    const totalPages = apiPagination.totalPages || 1;
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const current = apiPage;
    if (current <= 4) {
      return [1, 2, 3, 4, 5, "...", totalPages];
    }
    if (current >= totalPages - 3) {
      return [1, "...", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    }
    return [1, "...", current - 1, current, current + 1, "...", totalPages];
  }, [apiPage, apiPagination.totalPages]);

  const handleExportApiCsv = async () => {
    if (apiPagination.total === 0) return;
    try {
      let rowsToExport = apiDetails;
      if (apiPagination.total > apiDetails.length) {
        const res = await campaignService.getApiDetails({
          page: 1,
          limit: 10000,
          search: apiSearch || undefined,
          status: apiStatusFilter !== "all" ? apiStatusFilter : undefined,
          date: apiDatePreset === "all" ? "all" : undefined,
        });
        rowsToExport = res?.data || (Array.isArray(res) ? res : apiDetails);
      }
      exportToCsv("API_Campaign_Report", rowsToExport, [
        { label: "Recipient", key: (r) => `+${r.str_recipient_number || ""}` },
        { label: "Sender", key: "str_sender_number" },
        { label: "Template", key: "str_template_name" },
        { label: "Status", key: "str_status" },
        { label: "Operator Message ID", key: "str_operator_message_id" },
        { label: "Cost", key: (r) => r.dbl_cost ?? 0 },
        { label: "Error", key: "str_error_message" },
        { label: "Dispatched At", key: "dttm_created_on" },
      ]);
    } catch (e) {
      console.error("Export API CSV error:", e);
    }
  };

  // ──────────────────────────────────────────────────────────────────────────
  // CSV Export Utility
  // ──────────────────────────────────────────────────────────────────────────
  const exportToCsv = (filename, rows, headers) => {
    if (!rows || !rows.length) return;
    const headerRow = headers.map((h) => `"${h.label}"`).join(",");
    const dataRows = rows.map((r) =>
      headers
        .map((h) => {
          const val = typeof h.key === "function" ? h.key(r) : r[h.key];
          return `"${String(val || "").replace(/"/g, '""')}"`;
        })
        .join(",")
    );
    const csvContent = "data:text/csv;charset=utf-8," + [headerRow, ...dataRows].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `${filename}_${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6 pb-16">

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* TAB 1: CAMPAIGN REPORT                                               */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === "campaigns" && (
        <div className="space-y-6">
          {/* Campaign Selector Toolbar */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-5 shadow-xs">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                  Select Broadcast Campaign
                </label>
                <select
                  value={selectedCampaignId}
                  onChange={(e) => handleCampaignSelect(e.target.value)}
                  className="py-2.5 px-3.5 text-xs bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 font-semibold text-gray-900 dark:text-white min-w-[280px]"
                >
                  {campaignsList.length === 0 ? (
                    <option value="">No campaigns available</option>
                  ) : (
                    campaignsList.map((c) => (
                      <option
                        key={c._id || c.int_campaign_id}
                        value={c.int_campaign_id || c._id}
                      >
                        {c.str_campaign_name} ({c.str_template_name || "Template"} •{" "}
                        {c.str_status || "DRAFT"})
                      </option>
                    ))
                  )}
                </select>
              </div>

              {selectedCampaign && (
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      const exportCols = [
                        { label: "Recipient", key: "str_recipient_number" },
                        { label: "Sender Number", key: "str_sender_number" },
                        { label: "Status", key: "str_status" },
                        ...(showCredits ? [{ label: "Cost", key: "dbl_cost" }] : []),
                        { label: "Template", key: "str_template_name" },
                        { label: "Msg ID", key: "str_operator_message_id" },
                        { label: "Created On", key: "dttm_created_on" },
                        { label: "Sent On", key: "dttm_sent_on" },
                        { label: "Delivered On", key: "dttm_delivered_on" },
                        { label: "Read On", key: "dttm_read_on" },
                        { label: "Error Message", key: "str_error_message" },
                      ];
                      const rowsWithSender = (displayedMessages || campaignMessages).map((m) => ({
                        ...m,
                        str_sender_number: m.str_sender_number || m.str_phone_number_id || selectedCampaign?.str_sender_number || "",
                      }));
                      exportToCsv(
                        `Campaign_${selectedCampaign.str_campaign_name}`,
                        rowsWithSender,
                        exportCols
                      );
                    }}
                    disabled={campaignMessages.length === 0}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 disabled:opacity-50 text-gray-800 dark:text-gray-200 font-semibold rounded-xl text-xs transition-colors cursor-pointer"
                  >
                    <ArrowDownTrayIcon className="w-4 h-4" />
                    <span>Export CSV</span>
                  </button>

                  <button
                    type="button"
                    onClick={loadSelectedCampaignReport}
                    className="p-2 text-gray-500 hover:text-blue-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors cursor-pointer"
                    title="Refresh data"
                  >
                    <ArrowPathIcon className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>

            {/* Campaign Summary Info Banner */}
            {selectedCampaign && (() => {
              const senders = Array.isArray(selectedCampaign.arr_sender_numbers) && selectedCampaign.arr_sender_numbers.length > 0
                ? selectedCampaign.arr_sender_numbers
                : (selectedCampaign.str_sender_number ? [selectedCampaign.str_sender_number] : (selectedCampaign.str_phone_number_id ? [selectedCampaign.str_phone_number_id] : []));
              const isMulti =
                (selectedCampaign.str_sender_selection_mode || "").toUpperCase() === "MULTIPLE" ||
                senders.length > 1;
              const isDiff = (selectedCampaign.str_sender_distribution_mode || "").toUpperCase() === "DIFFERENT";

              return (
                <div className="mt-5 pt-4 border-t border-gray-100 dark:border-gray-700 grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs">
                  <div>
                    <span className="text-gray-400 block text-[11px]">Campaign Title</span>
                    <span className="font-bold text-gray-900 dark:text-white">
                      {selectedCampaign.str_campaign_name}
                    </span>
                    <span className="text-[10px] text-gray-400 font-mono block mt-0.5">
                      ID: {selectedCampaign.str_campaign_id || `#${selectedCampaign.int_campaign_id}`}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-400 block text-[11px]">Template Used</span>
                    <span className="font-semibold text-gray-800 dark:text-gray-200 font-mono">
                      {selectedCampaign.str_template_name}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-400 block text-[11px] mb-0.5">
                      {isMulti && senders.length > 1 ? "Sender Numbers (Distributed)" : "Sender Number"}
                    </span>
                    {isMulti && senders.length > 1 ? (
                      <div className="flex flex-col gap-1 items-start">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800">
                            <PhoneIcon className="w-2.5 h-2.5" />
                            {senders.length} Senders
                          </span>
                          <span
                            className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide border ${
                              isDiff
                                ? "bg-purple-50 text-purple-700 dark:bg-purple-950/50 dark:text-purple-300 border-purple-200 dark:border-purple-800"
                                : "bg-blue-50 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300 border-blue-200 dark:border-blue-800"
                            }`}
                          >
                            {isDiff ? "Diff Dist." : "Same Dist."}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-1 mt-0.5">
                          {senders.map((s) => (
                            <span
                              key={s}
                              className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-mono font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-600"
                            >
                              +{String(s).replace(/^\+/, "")}
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <span className="font-semibold text-gray-800 dark:text-gray-200 font-mono text-xs">
                        +{String(senders[0] || selectedCampaign.str_sender_number || "Default").replace(/^\+/, "")}
                      </span>
                    )}
                  </div>
                  <div>
                    <span className="text-gray-400 block text-[11px]">Campaign Status</span>
                    <span
                      className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase border ${
                        STATUS_PILL_STYLES[selectedCampaign.str_status] ||
                        STATUS_PILL_STYLES.DRAFT
                      }`}
                    >
                      {selectedCampaign.str_status || "DRAFT"}
                    </span>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* KPI Stat Cards */}
          {selectedCampaign && (
            <div className={`grid grid-cols-2 ${showCredits ? "sm:grid-cols-6" : "sm:grid-cols-5"} gap-3.5`}>
              <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-xs">
                <span className="text-[11px] font-bold text-gray-400 uppercase">Total Audience</span>
                <p className="text-2xl font-black text-gray-900 dark:text-white mt-1">
                  {selectedCampaign.int_recipient_count || selectedCampaign.int_message_count || 0}
                </p>
              </div>

              <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-xs">
                <span className="text-[11px] font-bold text-teal-600 dark:text-teal-400 uppercase">Sent</span>
                <p className="text-2xl font-black text-teal-600 dark:text-teal-400 mt-1">
                  {selectedCampaign.int_sent_count || 0}
                </p>
              </div>

              <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-xs">
                <span className="text-[11px] font-bold text-emerald-600 dark:text-emerald-400 uppercase">Delivered</span>
                <p className="text-2xl font-black text-emerald-600 dark:text-emerald-400 mt-1">
                  {selectedCampaign.int_delivered_count || 0}
                </p>
              </div>

              <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-xs">
                <span className="text-[11px] font-bold text-blue-600 dark:text-blue-400 uppercase">Read</span>
                <p className="text-2xl font-black text-blue-600 dark:text-blue-400 mt-1">
                  {selectedCampaign.int_read_count || 0}
                </p>
              </div>

              <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-xs">
                <span className="text-[11px] font-bold text-rose-600 dark:text-rose-400 uppercase">Failed</span>
                <p className="text-2xl font-black text-rose-600 dark:text-rose-400 mt-1">
                  {selectedCampaign.int_failed_count || 0}
                </p>
              </div>

              {showCredits && (
                <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-xs">
                  <span className="text-[11px] font-bold text-indigo-600 dark:text-indigo-400 uppercase">Total Debited</span>
                  <p className="text-2xl font-black text-indigo-600 dark:text-indigo-400 mt-1">
                    ₹{(selectedCampaign.dbl_total_cost != null ? Number(selectedCampaign.dbl_total_cost) : 0).toFixed(2)}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Delivery Log Table Card */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-xs overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-bold text-gray-900 dark:text-white">
                  Recipient Delivery Breakdown
                </h3>
                <p className="text-xs text-gray-400 mt-0.5">
                  Detailed status for each individual audience recipient
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2.5">
                <div className="relative w-full sm:w-56">
                  <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search mobile number..."
                    value={campaignSearch}
                    onChange={(e) => setCampaignSearch(e.target.value)}
                    className="w-full pl-9 pr-3 py-1.5 text-xs bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-white"
                  />
                </div>

                {/* Sender Number Filter Dropdown */}
                <select
                  value={campaignSenderFilter}
                  onChange={(e) => setCampaignSenderFilter(e.target.value)}
                  className="py-1.5 px-3 text-xs bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 text-gray-800 dark:text-gray-200 font-mono"
                  title="Filter recipients by sender number"
                >
                  <option value="all">
                    All Senders {availableCampaignSenders.length > 1 ? `(${availableCampaignSenders.length})` : ""}
                  </option>
                  {availableCampaignSenders.map((s) => (
                    <option key={s} value={s}>
                      +{s}
                    </option>
                  ))}
                </select>

                <select
                  value={campaignStatusFilter}
                  onChange={(e) => setCampaignStatusFilter(e.target.value)}
                  className="py-1.5 px-3 text-xs bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 text-gray-800 dark:text-gray-200"
                >
                  <option value="all">All Status</option>
                  <option value="SENT">Sent</option>
                  <option value="DELIVERED">Delivered</option>
                  <option value="READ">Read</option>
                  <option value="FAILED">Failed</option>
                  <option value="QUEUED">Queued</option>
                </select>
              </div>
            </div>

            <div className="overflow-x-auto">
              {loadingCampaignData ? (
                <div className="py-16 text-center">
                  <span className="inline-block w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></span>
                  <p className="mt-2 text-xs font-medium text-gray-500">Loading delivery breakdown...</p>
                </div>
              ) : displayedMessages.length === 0 ? (
                <div className="py-14 text-center">
                  <div className="text-3xl mb-2">📋</div>
                  <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                    No delivery records found {campaignSenderFilter !== "all" ? `for sender +${campaignSenderFilter}` : "for this campaign"}
                  </p>
                  <p className="text-[11px] text-gray-400 mt-0.5">
                    {campaignSenderFilter !== "all"
                      ? "Try selecting 'All Senders' to view recipients dispatched by other numbers."
                      : "Messages will appear here as soon as the campaign is dispatched."}
                  </p>
                </div>
              ) : (
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="border-b border-gray-100 dark:border-gray-700/60 bg-gray-50/50 dark:bg-gray-800/40 text-[11px] font-bold uppercase tracking-wider text-gray-400">
                      <th className="py-3 px-4">#</th>
                      <th className="py-3 px-4">Recipient Number</th>
                      <th className="py-3 px-4">Sender Number</th>
                      <th className="py-3 px-4">Delivery Status</th>
                      {showCredits && <th className="py-3 px-4">Rate / Cost</th>}
                      <th className="py-3 px-4">Message ID</th>
                      <th className="py-3 px-4">Sent Time</th>
                      <th className="py-3 px-4">Delivered Time</th>
                      <th className="py-3 px-4">Error / Notes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50 font-medium">
                    {displayedMessages.map((msg, idx) => {
                      const st = (msg.str_status || "QUEUED").toUpperCase();
                      const senderNum = msg.str_sender_number || msg.str_phone_number_id || selectedCampaign?.str_sender_number;
                      return (
                        <tr
                          key={msg._id || idx}
                          className="hover:bg-gray-50/80 dark:hover:bg-gray-700/30 transition-colors"
                        >
                          <td className="py-3 px-4 text-gray-400 text-[11px] font-mono">
                            {idx + 1}
                          </td>
                          <td className="py-3 px-4 font-bold text-gray-900 dark:text-white font-mono">
                            +{String(msg.str_recipient_number).replace(/^\+/, "")}
                          </td>
                          <td className="py-3 px-4 font-mono text-gray-700 dark:text-gray-300 text-[11px]">
                            {senderNum ? (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700/60 text-gray-800 dark:text-gray-200 font-mono text-[11px] border border-gray-200 dark:border-gray-600">
                                +{String(senderNum).replace(/^\+/, "")}
                              </span>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <span
                              className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase border ${
                                STATUS_PILL_STYLES[st] || STATUS_PILL_STYLES.QUEUED
                              }`}
                            >
                              {st}
                            </span>
                          </td>
                          {showCredits && (
                            <td className="py-3 px-4 font-mono font-bold text-emerald-600 dark:text-emerald-400 text-[11px]">
                              {msg.dbl_cost != null && Number(msg.dbl_cost) > 0
                                ? `₹${Number(msg.dbl_cost).toFixed(4)}`
                                : "—"}
                            </td>
                          )}
                          <td className="py-3 px-4 font-mono text-[11px] text-gray-500">
                            {msg.str_operator_message_id || msg.str_message_id || "—"}
                          </td>
                          <td className="py-3 px-4 text-gray-500 text-[11px]">
                            {msg.dttm_sent_on
                              ? new Date(msg.dttm_sent_on).toLocaleString()
                              : "—"}
                          </td>
                          <td className="py-3 px-4 text-gray-500 text-[11px]">
                            {msg.dttm_delivered_on
                              ? new Date(msg.dttm_delivered_on).toLocaleString()
                              : "—"}
                          </td>
                          <td className="py-3 px-4 text-[11px] text-rose-500 max-w-xs truncate">
                            {msg.str_error_message || msg.str_error_code || "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* TAB 2: GENERAL REPORT                                                */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === "general" && (
        <div className="space-y-6">
          {/* Top Control Bar & Date Range Filter */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-5 shadow-xs">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <span>General Messages Report</span>
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Direct message delivery records dispatched without a broadcast campaign name.
                </p>
              </div>

              {/* Action Buttons: Export & Refresh */}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleExportGeneralCsv}
                  disabled={generalPagination.total === 0}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 disabled:opacity-50 text-gray-800 dark:text-gray-200 font-semibold rounded-xl text-xs transition-colors cursor-pointer"
                >
                  <ArrowDownTrayIcon className="w-4 h-4" />
                  <span>Export CSV</span>
                </button>

                <button
                  type="button"
                  onClick={loadGeneralReport}
                  className="p-2 text-gray-500 hover:text-blue-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors cursor-pointer"
                  title="Refresh data"
                >
                  <ArrowPathIcon className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Date Range Toolbar */}
            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700/60 flex flex-wrap items-center justify-between gap-3">
              {/* Preset Buttons */}
              <div className="flex flex-wrap items-center bg-gray-100 dark:bg-gray-700/60 p-1 rounded-xl gap-1">
                <button
                  type="button"
                  onClick={() => handleDatePreset("today")}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                    generalDatePreset === "today"
                      ? "bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-xs"
                      : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                  }`}
                >
                  Today
                </button>
                <button
                  type="button"
                  onClick={() => handleDatePreset("yesterday")}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                    generalDatePreset === "yesterday"
                      ? "bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-xs"
                      : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                  }`}
                >
                  Yesterday
                </button>
                <button
                  type="button"
                  onClick={() => handleDatePreset("7days")}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                    generalDatePreset === "7days"
                      ? "bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-xs"
                      : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                  }`}
                >
                  Last 7 Days
                </button>
                <button
                  type="button"
                  onClick={() => handleDatePreset("thisMonth")}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                    generalDatePreset === "thisMonth"
                      ? "bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-xs"
                      : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                  }`}
                >
                  This Month
                </button>
                <button
                  type="button"
                  onClick={() => handleDatePreset("all")}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                    generalDatePreset === "all"
                      ? "bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-xs"
                      : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                  }`}
                >
                  All Dates
                </button>
              </div>

              {/* Custom From & To Inputs */}
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 px-3 py-1.5 rounded-xl">
                  <span className="text-[11px] font-bold text-gray-400 uppercase">From:</span>
                  <input
                    type="date"
                    value={generalStartDate || ""}
                    onChange={(e) => handleStartDateChange(e.target.value)}
                    className="text-xs bg-transparent border-none p-0 focus:ring-0 font-semibold text-gray-800 dark:text-gray-200 cursor-pointer"
                  />
                </div>

                <span className="text-gray-400 text-xs font-bold">—</span>

                <div className="flex items-center gap-1.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 px-3 py-1.5 rounded-xl">
                  <span className="text-[11px] font-bold text-gray-400 uppercase">To:</span>
                  <input
                    type="date"
                    value={generalEndDate || ""}
                    onChange={(e) => handleEndDateChange(e.target.value)}
                    className="text-xs bg-transparent border-none p-0 focus:ring-0 font-semibold text-gray-800 dark:text-gray-200 cursor-pointer"
                  />
                </div>
              </div>
            </div>

            {/* Quick Date Display Banner */}
            <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700/60 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
              <div className="flex items-center gap-2">
                <CalendarIcon className="w-4 h-4 text-blue-500 shrink-0" />
                <span>
                  Showing data for:{" "}
                  <strong className="text-gray-800 dark:text-gray-200">
                    {getGeneralDateRangeLabel()}
                  </strong>
                </span>
              </div>
              <div>
                <span>
                  Total records in range:{" "}
                  <strong className="text-gray-800 dark:text-gray-200">{generalStats.total}</strong>
                </span>
              </div>
            </div>
          </div>

          {/* KPI Stat Cards for General Messages */}
          <div className={`grid grid-cols-2 ${showCredits ? "sm:grid-cols-4 lg:grid-cols-7" : "sm:grid-cols-3 lg:grid-cols-6"} gap-3.5`}>
            <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-xs">
              <span className="text-[11px] font-bold text-gray-400 uppercase">Total Messages</span>
              <p className="text-2xl font-black text-gray-900 dark:text-white mt-1">
                {generalStats.total || 0}
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-xs">
              <span className="text-[11px] font-bold text-teal-500 uppercase">Sent</span>
              <p className="text-2xl font-black text-teal-600 dark:text-teal-400 mt-1">
                {generalStats.sent || 0}
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-xs">
              <span className="text-[11px] font-bold text-emerald-500 uppercase">Delivered</span>
              <p className="text-2xl font-black text-emerald-600 dark:text-emerald-400 mt-1">
                {generalStats.delivered || 0}
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-xs">
              <span className="text-[11px] font-bold text-blue-500 uppercase">Read</span>
              <p className="text-2xl font-black text-blue-600 dark:text-blue-400 mt-1">
                {generalStats.read || 0}
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-xs">
              <span className="text-[11px] font-bold text-rose-500 uppercase">Failed</span>
              <p className="text-2xl font-black text-rose-600 dark:text-rose-400 mt-1">
                {generalStats.failed || 0}
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-xs">
              <span className="text-[11px] font-bold text-amber-500 uppercase">Queued / Pending</span>
              <p className="text-2xl font-black text-amber-600 dark:text-amber-400 mt-1">
                {(generalStats.queued || 0) + (generalStats.pending || 0)}
              </p>
            </div>
            {showCredits && (
              <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-xs">
                <span className="text-[11px] font-bold text-indigo-500 uppercase">Total Debited</span>
                <p className="text-xl font-black text-indigo-600 dark:text-indigo-400 mt-1 font-mono">
                  ₹{Number(generalStats.total_cost || 0).toFixed(4)}
                </p>
              </div>
            )}
          </div>

          {/* Message Transmission Table */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-xs overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h4 className="text-sm font-bold text-gray-900 dark:text-white">
                  Message Delivery Breakdown
                </h4>
                <p className="text-xs text-gray-400 mt-0.5">
                  Detailed logs of individual message transmissions with delivery milestones
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2.5">
                <div className="relative w-full sm:w-60">
                  <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search number, template, ID..."
                    value={generalSearch}
                    onChange={(e) => handleGeneralSearchChange(e.target.value)}
                    className="w-full pl-9 pr-3 py-1.5 text-xs bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-white"
                  />
                </div>

                <select
                  value={generalStatusFilter}
                  onChange={(e) => handleGeneralStatusChange(e.target.value)}
                  className="py-1.5 px-3 text-xs bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 text-gray-800 dark:text-gray-200 cursor-pointer font-medium"
                >
                  <option value="all">All Status</option>
                  <option value="SENT">Sent</option>
                  <option value="DELIVERED">Delivered</option>
                  <option value="READ">Read</option>
                  <option value="FAILED">Failed</option>
                  <option value="PENDING">Pending</option>
                  <option value="QUEUED">Queued</option>
                </select>
              </div>
            </div>

            <div className="overflow-x-auto">
              {loadingGeneral ? (
                <div className="py-16 text-center">
                  <span className="inline-block w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></span>
                  <p className="mt-2 text-xs font-medium text-gray-500">Loading general transmission logs...</p>
                </div>
              ) : generalDetails.length === 0 ? (
                <div className="py-14 text-center">
                  <div className="text-3xl mb-2">📑</div>
                  <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                    No messages found for this date range
                  </p>
                  <p className="text-[11px] text-gray-400 mt-0.5">
                    {generalDatePreset === "all"
                      ? "No general messages have been sent yet."
                      : `No general messages recorded for the selected period (${getGeneralDateRangeLabel()}). Try choosing another date range or "All Dates".`}
                  </p>
                </div>
              ) : (
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="border-b border-gray-100 dark:border-gray-700/60 bg-gray-50/50 dark:bg-gray-800/40 text-[11px] font-bold uppercase tracking-wider text-gray-400">
                      <th className="py-3 px-3.5">#</th>
                      <th className="py-3 px-3.5">Recipient</th>
                      <th className="py-3 px-3.5">Template</th>
                      <th className="py-3 px-3.5">Status</th>
                      {showCredits && <th className="py-3 px-3.5">Rate / Cost</th>}
                      <th className="py-3 px-3.5">Msg ID</th>
                      <th className="py-3 px-3.5">Sent At</th>
                      <th className="py-3 px-3.5">Delivered At</th>
                      <th className="py-3 px-3.5">Read At</th>
                      <th className="py-3 px-3.5">Error Details</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50 font-medium">
                    {generalDetails.map((item, idx) => {
                      const st = (item.str_status || "QUEUED").toUpperCase();
                      const isFailed = st === "FAILED";
                      const rowNumber = (generalPage - 1) * generalLimit + idx + 1;
                      const sentTime = item.dttm_sent_on || ((st === "SENT" || st === "SEND") ? item.dttm_created_on : null);
                      const deliveredTime = item.dttm_delivered_on;
                      const readTime = item.dttm_read_on;
                      const hasError = isFailed || Boolean(item.str_error_message) || Boolean(item.str_error_code);

                      return (
                        <tr
                          key={item._id || idx}
                          className="hover:bg-gray-50/80 dark:hover:bg-gray-700/30 transition-colors"
                        >
                          <td className="py-3 px-3.5 text-gray-400 text-[11px] font-mono">
                            {rowNumber}
                          </td>
                          <td className="py-3 px-3.5 font-bold text-gray-900 dark:text-white font-mono">
                            +{item.str_recipient_number}
                          </td>
                          <td className="py-3 px-3.5 font-medium text-gray-700 dark:text-gray-300">
                            {item.str_template_name || "—"}
                          </td>
                          <td className="py-3 px-3.5">
                            <span
                              className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase border ${
                                STATUS_PILL_STYLES[st] || STATUS_PILL_STYLES.QUEUED
                              }`}
                            >
                              {st}
                            </span>
                          </td>
                          {showCredits && (
                            <td className="py-3 px-3.5 font-mono text-[11px] text-gray-700 dark:text-gray-300">
                              {item.dbl_cost != null ? `₹${Number(item.dbl_cost).toFixed(4)}` : "—"}
                            </td>
                          )}
                          <td className="py-3 px-3.5 font-mono text-[11px] text-gray-500 max-w-[130px] truncate" title={item.str_operator_message_id}>
                            {item.str_operator_message_id || "—"}
                          </td>
                          {/* Sent At */}
                          <td className="py-3 px-3.5 text-gray-600 dark:text-gray-300 text-[11px] whitespace-nowrap">
                            {sentTime ? (
                              <div className="flex flex-col">
                                <span className="font-medium text-gray-800 dark:text-gray-200">
                                  {new Date(sentTime).toLocaleDateString()}
                                </span>
                                <span className="text-[10px] text-gray-400 font-mono">
                                  {new Date(sentTime).toLocaleTimeString()}
                                </span>
                              </div>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          {/* Delivered At */}
                          <td className="py-3 px-3.5 text-[11px] whitespace-nowrap">
                            {deliveredTime ? (
                              <div className="flex flex-col">
                                <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                                  {new Date(deliveredTime).toLocaleDateString()}
                                </span>
                                <span className="text-[10px] text-emerald-500/80 font-mono">
                                  {new Date(deliveredTime).toLocaleTimeString()}
                                </span>
                              </div>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          {/* Read At */}
                          <td className="py-3 px-3.5 text-[11px] whitespace-nowrap">
                            {readTime ? (
                              <div className="flex flex-col">
                                <span className="font-semibold text-blue-600 dark:text-blue-400">
                                  {new Date(readTime).toLocaleDateString()}
                                </span>
                                <span className="text-[10px] text-blue-500/80 font-mono">
                                  {new Date(readTime).toLocaleTimeString()}
                                </span>
                              </div>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          {/* Error Details */}
                          <td className="py-3 px-3.5 text-[11px]">
                            {hasError ? (
                              <div className="flex items-center gap-1.5 max-w-[230px]">
                                <div className="min-w-0 flex-1">
                                  {item.str_error_code && (
                                    <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-mono font-bold bg-rose-100 dark:bg-rose-950/70 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-800 mr-1">
                                      {item.str_error_code}
                                    </span>
                                  )}
                                  <span
                                    className="text-rose-600 dark:text-rose-400 font-medium truncate inline-block max-w-[130px] align-middle"
                                    title={item.str_error_message || item.str_error_code}
                                  >
                                    {item.str_error_message || "Failed"}
                                  </span>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setSelectedGeneralError(item)}
                                  className="px-2 py-0.5 rounded-lg bg-rose-50 dark:bg-rose-950/60 text-rose-600 dark:text-rose-400 border border-rose-200 dark:border-rose-800 hover:bg-rose-100 dark:hover:bg-rose-900/40 text-[10px] font-bold cursor-pointer transition-colors shrink-0 shadow-2xs"
                                  title="View full error details"
                                >
                                  Details
                                </button>
                              </div>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination Controls */}
            {!loadingGeneral && generalPagination.total > 0 && (
              <div className="p-4 border-t border-gray-100 dark:border-gray-700/60 bg-gray-50/50 dark:bg-gray-800/40 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                  <span>
                    Showing{" "}
                    <strong className="text-gray-800 dark:text-gray-200">
                      {(generalPage - 1) * generalLimit + 1}
                    </strong>{" "}
                    to{" "}
                    <strong className="text-gray-800 dark:text-gray-200">
                      {Math.min(generalPage * generalLimit, generalPagination.total)}
                    </strong>{" "}
                    of{" "}
                    <strong className="text-gray-800 dark:text-gray-200">
                      {generalPagination.total}
                    </strong>{" "}
                    records
                  </span>

                  <div className="flex items-center gap-1.5 ml-2">
                    <span className="text-[11px] text-gray-400">Per page:</span>
                    <select
                      value={generalLimit}
                      onChange={(e) => {
                        setGeneralLimit(Number(e.target.value));
                        setGeneralPage(1);
                      }}
                      className="py-1 px-2 text-xs bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 text-gray-800 dark:text-gray-200 font-medium"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                </div>

                {generalPagination.totalPages > 1 && (
                  <div className="flex items-center gap-1">
                    {/* First Page */}
                    <button
                      type="button"
                      onClick={() => setGeneralPage(1)}
                      disabled={generalPage === 1}
                      className="px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-xs font-medium cursor-pointer"
                      title="First Page"
                    >
                      «
                    </button>

                    {/* Previous Page */}
                    <button
                      type="button"
                      onClick={() => setGeneralPage((p) => Math.max(1, p - 1))}
                      disabled={generalPage === 1}
                      className="p-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                      title="Previous Page"
                    >
                      <ChevronLeftIcon className="w-4 h-4" />
                    </button>

                    {/* Page Numbers */}
                    {generalPageNumbers.map((page, idx) => {
                      if (page === "...") {
                        return (
                          <span key={`ell-${idx}`} className="px-2 text-gray-400 select-none text-xs">
                            ...
                          </span>
                        );
                      }
                      return (
                        <button
                          key={page}
                          type="button"
                          onClick={() => setGeneralPage(page)}
                          className={`min-w-[32px] h-8 px-2 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                            generalPage === page
                              ? "bg-blue-600 text-white shadow-xs shadow-blue-500/30"
                              : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700"
                          }`}
                        >
                          {page}
                        </button>
                      );
                    })}

                    {/* Next Page */}
                    <button
                      type="button"
                      onClick={() => setGeneralPage((p) => Math.min(generalPagination.totalPages, p + 1))}
                      disabled={generalPage === generalPagination.totalPages || generalPagination.totalPages === 0}
                      className="p-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                      title="Next Page"
                    >
                      <ChevronRightIcon className="w-4 h-4" />
                    </button>

                    {/* Last Page */}
                    <button
                      type="button"
                      onClick={() => setGeneralPage(generalPagination.totalPages)}
                      disabled={generalPage === generalPagination.totalPages || generalPagination.totalPages === 0}
                      className="px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-xs font-medium cursor-pointer"
                      title="Last Page"
                    >
                      »
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* TAB 3: API REPORT                                                    */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === "api" && (
        <div className="space-y-6">
          {/* Top Control Bar & Date Range Presets */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-5 shadow-xs">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <span>Programmatic API Campaign Logs</span>
                  <span className="text-[11px] font-mono font-semibold bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-2 py-0.5 rounded-md">
                    tbl_api_campaign_details
                  </span>
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  High-throughput transactional & API message delivery details dispatched via developer API keys or webhooks.
                </p>
              </div>

              {/* Action Buttons: Export CSV & Refresh */}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleExportApiCsv}
                  disabled={apiPagination.total === 0}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 disabled:opacity-50 text-gray-800 dark:text-gray-200 font-semibold rounded-xl text-xs transition-colors cursor-pointer"
                >
                  <ArrowDownTrayIcon className="w-4 h-4" />
                  <span>Export CSV</span>
                </button>

                <button
                  type="button"
                  onClick={loadApiReport}
                  className="p-2 text-gray-500 hover:text-blue-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors cursor-pointer"
                  title="Refresh data"
                >
                  <ArrowPathIcon className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Date Range Toolbar */}
            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700/60 flex flex-wrap items-center justify-between gap-3">
              {/* Preset Buttons */}
              <div className="flex flex-wrap items-center bg-gray-100 dark:bg-gray-700/60 p-1 rounded-xl gap-1">
                <button
                  type="button"
                  onClick={() => handleApiDatePreset("all")}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                    apiDatePreset === "all"
                      ? "bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-xs"
                      : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                  }`}
                >
                  All Time
                </button>
                <button
                  type="button"
                  onClick={() => handleApiDatePreset("today")}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                    apiDatePreset === "today"
                      ? "bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-xs"
                      : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                  }`}
                >
                  Today
                </button>
                <button
                  type="button"
                  onClick={() => handleApiDatePreset("yesterday")}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                    apiDatePreset === "yesterday"
                      ? "bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-xs"
                      : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                  }`}
                >
                  Yesterday
                </button>
                <button
                  type="button"
                  onClick={() => handleApiDatePreset("7days")}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                    apiDatePreset === "7days"
                      ? "bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-xs"
                      : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                  }`}
                >
                  Last 7 Days
                </button>
                <button
                  type="button"
                  onClick={() => handleApiDatePreset("thisMonth")}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                    apiDatePreset === "thisMonth"
                      ? "bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-xs"
                      : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                  }`}
                >
                  This Month
                </button>
              </div>

              {/* Custom Date Form */}
              <form onSubmit={handleApiCustomDateApply} className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 px-2.5 py-1.5 rounded-xl text-xs">
                  <CalendarIcon className="w-3.5 h-3.5 text-gray-400" />
                  <input
                    type="date"
                    value={apiStartDate}
                    onChange={(e) => setApiStartDate(e.target.value)}
                    className="bg-transparent text-gray-800 dark:text-gray-200 focus:outline-hidden text-xs cursor-pointer"
                  />
                  <span className="text-gray-400">to</span>
                  <input
                    type="date"
                    value={apiEndDate}
                    onChange={(e) => setApiEndDate(e.target.value)}
                    className="bg-transparent text-gray-800 dark:text-gray-200 focus:outline-hidden text-xs cursor-pointer"
                  />
                </div>
                <button
                  type="submit"
                  className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-semibold transition-colors cursor-pointer"
                >
                  Apply
                </button>
              </form>
            </div>
          </div>

          {/* Metric KPI Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3.5">
            <div className="bg-white dark:bg-gray-800 p-4 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-xs">
              <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Total API Logs</span>
              <p className="text-xl font-black text-gray-900 dark:text-white mt-1">
                {apiStats.total.toLocaleString()}
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 p-4 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-xs">
              <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">Sent / Success</span>
              <p className="text-xl font-black text-emerald-600 dark:text-emerald-400 mt-1">
                {apiStats.sent.toLocaleString()}
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 p-4 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-xs">
              <span className="text-[10px] font-bold uppercase tracking-wider text-blue-600 dark:text-blue-400">Delivered</span>
              <p className="text-xl font-black text-blue-600 dark:text-blue-400 mt-1">
                {apiStats.delivered.toLocaleString()}
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 p-4 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-xs">
              <span className="text-[10px] font-bold uppercase tracking-wider text-purple-600 dark:text-purple-400">Read</span>
              <p className="text-xl font-black text-purple-600 dark:text-purple-400 mt-1">
                {apiStats.read.toLocaleString()}
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 p-4 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-xs">
              <span className="text-[10px] font-bold uppercase tracking-wider text-rose-600 dark:text-rose-400">Failed</span>
              <p className="text-xl font-black text-rose-600 dark:text-rose-400 mt-1">
                {apiStats.failed.toLocaleString()}
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 p-4 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-xs">
              <span className="text-[10px] font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400">Total Billed</span>
              <p className="text-xl font-black text-amber-600 dark:text-amber-400 mt-1">
                {apiStats.total_cost.toFixed(2)}
              </p>
            </div>
          </div>

          {/* Table Container */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-xs overflow-hidden">
            {/* Search and Filters Bar */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="relative w-full sm:w-72">
                <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search recipient, sender, template, MID..."
                  value={apiSearch}
                  onChange={(e) => {
                    setApiSearch(e.target.value);
                    setApiPage(1);
                  }}
                  className="w-full pl-9 pr-3 py-2 text-xs bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-white"
                />
              </div>

              <div className="flex items-center gap-2.5">
                <select
                  value={apiStatusFilter}
                  onChange={(e) => {
                    setApiStatusFilter(e.target.value);
                    setApiPage(1);
                  }}
                  className="py-2 px-3 text-xs bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 text-gray-800 dark:text-gray-200 font-medium"
                >
                  <option value="all">All Delivery Statuses</option>
                  <option value="SENT">Sent</option>
                  <option value="DELIVERED">Delivered</option>
                  <option value="READ">Read</option>
                  <option value="FAILED">Failed</option>
                  <option value="QUEUED">Queued</option>
                </select>
              </div>
            </div>

            {/* Table Content */}
            <div className="overflow-x-auto">
              {loadingApi ? (
                <div className="py-20 text-center">
                  <span className="inline-block w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></span>
                  <p className="mt-2 text-xs font-medium text-gray-500">Loading API transmission logs...</p>
                </div>
              ) : apiDetails.length === 0 ? (
                <div className="py-16 text-center">
                  <div className="text-3xl mb-2">⚡</div>
                  <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                    No programmatic API logs found
                  </p>
                  <p className="text-xs text-gray-400 mt-1 max-w-md mx-auto">
                    Messages dispatched via developer API keys or webhooks will appear in this log with delivery timestamps and provider MIDs.
                  </p>
                </div>
              ) : (
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="border-b border-gray-100 dark:border-gray-700/60 bg-gray-50/50 dark:bg-gray-800/40 text-[11px] font-bold uppercase tracking-wider text-gray-400">
                      <th className="py-3.5 px-4 w-12">#</th>
                      <th className="py-3.5 px-4">Recipient</th>
                      <th className="py-3.5 px-4">Sender Number</th>
                      <th className="py-3.5 px-4">Template Name</th>
                      <th className="py-3.5 px-4">Status</th>
                      <th className="py-3.5 px-4">Provider Msg ID</th>
                      <th className="py-3.5 px-4">Cost</th>
                      <th className="py-3.5 px-4">Dispatched At</th>
                      <th className="py-3.5 px-4 text-center w-20">Diagnostics</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50 font-medium">
                    {apiDetails.map((item, idx) => {
                      const st = (item.str_status || "QUEUED").toUpperCase();
                      const rowNum = (apiPage - 1) * apiLimit + idx + 1;
                      const hasError = Boolean(item.str_error_message || item.str_error_code);
                      return (
                        <tr
                          key={item._id || idx}
                          className="hover:bg-gray-50/80 dark:hover:bg-gray-700/30 transition-colors"
                        >
                          <td className="py-3.5 px-4 text-gray-400 text-[11px] font-mono">
                            {rowNum}
                          </td>
                          <td className="py-3.5 px-4 font-bold text-gray-900 dark:text-white font-mono">
                            +{item.str_recipient_number}
                          </td>
                          <td className="py-3.5 px-4 text-gray-600 dark:text-gray-300 font-mono text-[11px]">
                            {item.str_sender_number ? `+${item.str_sender_number}` : "—"}
                          </td>
                          <td className="py-3.5 px-4">
                            <span className="font-semibold text-gray-800 dark:text-gray-200 bg-gray-100 dark:bg-gray-700/60 px-2 py-0.5 rounded-md text-[11px]">
                              {item.str_template_name || "—"}
                            </span>
                          </td>
                          <td className="py-3.5 px-4">
                            <span
                              className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase border ${
                                STATUS_PILL_STYLES[st] || STATUS_PILL_STYLES.QUEUED
                              }`}
                            >
                              {st}
                            </span>
                          </td>
                          <td className="py-3.5 px-4 font-mono text-[11px] text-gray-500 max-w-[180px] truncate" title={item.str_operator_message_id || ""}>
                            {item.str_operator_message_id || item.str_message_id || "—"}
                          </td>
                          <td className="py-3.5 px-4 text-gray-600 dark:text-gray-300 font-mono text-[11px]">
                            {Number(item.dbl_cost ?? 0).toFixed(2)}
                          </td>
                          <td className="py-3.5 px-4 text-gray-500 text-[11px] whitespace-nowrap">
                            {item.dttm_created_on
                              ? new Date(item.dttm_created_on).toLocaleString()
                              : "—"}
                          </td>
                          <td className="py-3.5 px-4 text-center">
                            {hasError ? (
                              <button
                                type="button"
                                onClick={() => setSelectedApiError(item)}
                                className="px-2 py-1 text-[10px] font-bold text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-950/40 hover:bg-rose-100 dark:hover:bg-rose-900/60 rounded-md transition-colors cursor-pointer"
                              >
                                View Error
                              </button>
                            ) : (
                              <span className="text-gray-300 dark:text-gray-600">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination Controls */}
            <div className="p-4 border-t border-gray-100 dark:border-gray-700/60 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs bg-gray-50/40 dark:bg-gray-800/40">
              <div className="flex items-center gap-3 text-gray-500 dark:text-gray-400">
                <span>
                  Showing{" "}
                  <strong className="text-gray-900 dark:text-white">
                    {apiPagination.total === 0 ? 0 : (apiPage - 1) * apiLimit + 1}
                  </strong>{" "}
                  to{" "}
                  <strong className="text-gray-900 dark:text-white">
                    {Math.min(apiPage * apiLimit, apiPagination.total)}
                  </strong>{" "}
                  of{" "}
                  <strong className="text-gray-900 dark:text-white">
                    {apiPagination.total.toLocaleString()}
                  </strong>{" "}
                  API messages
                </span>

                <div className="flex items-center gap-1.5 ml-2 border-l border-gray-200 dark:border-gray-700 pl-3">
                  <span className="text-[11px]">Rows per page:</span>
                  <select
                    value={apiLimit}
                    onChange={(e) => {
                      setApiLimit(Number(e.target.value));
                      setApiPage(1);
                    }}
                    className="py-1 px-2 text-xs bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 text-gray-800 dark:text-gray-200 font-medium"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>

              {apiPagination.totalPages > 1 && (
                <div className="flex items-center gap-1">
                  {/* First Page */}
                  <button
                    type="button"
                    onClick={() => setApiPage(1)}
                    disabled={apiPage === 1}
                    className="px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-xs font-medium cursor-pointer"
                    title="First Page"
                  >
                    «
                  </button>

                  {/* Previous Page */}
                  <button
                    type="button"
                    onClick={() => setApiPage((p) => Math.max(1, p - 1))}
                    disabled={apiPage === 1}
                    className="p-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                    title="Previous Page"
                  >
                    <ChevronLeftIcon className="w-4 h-4" />
                  </button>

                  {/* Page Numbers */}
                  {apiPageNumbers.map((page, idx) => {
                    if (page === "...") {
                      return (
                        <span key={`api-ell-${idx}`} className="px-2 text-gray-400 select-none text-xs">
                          ...
                        </span>
                      );
                    }
                    return (
                      <button
                        key={`api-page-${page}`}
                        type="button"
                        onClick={() => setApiPage(page)}
                        className={`min-w-[32px] h-8 px-2 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                          apiPage === page
                            ? "bg-blue-600 text-white shadow-xs shadow-blue-500/30"
                            : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700"
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}

                  {/* Next Page */}
                  <button
                    type="button"
                    onClick={() => setApiPage((p) => Math.min(apiPagination.totalPages, p + 1))}
                    disabled={apiPage === apiPagination.totalPages || apiPagination.totalPages === 0}
                    className="p-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                    title="Next Page"
                  >
                    <ChevronRightIcon className="w-4 h-4" />
                  </button>

                  {/* Last Page */}
                  <button
                    type="button"
                    onClick={() => setApiPage(apiPagination.totalPages)}
                    disabled={apiPage === apiPagination.totalPages || apiPagination.totalPages === 0}
                    className="px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-xs font-medium cursor-pointer"
                    title="Last Page"
                  >
                    »
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* General Message Error Details Modal */}
      {selectedGeneralError && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs animate-in fade-in duration-150">
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-2xl max-w-lg w-full overflow-hidden">
            {/* Modal Header */}
            <div className="p-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between bg-rose-50/60 dark:bg-rose-950/20">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-rose-100 dark:bg-rose-900/60 flex items-center justify-center text-rose-600 dark:text-rose-400 shrink-0">
                  <ExclamationTriangleIcon className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-gray-900 dark:text-white">
                    Delivery Diagnostics & Error Details
                  </h3>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400 font-mono">
                    Recipient: +{selectedGeneralError.str_recipient_number}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSelectedGeneralError(null)}
                className="p-1.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition-colors"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-5 space-y-4 text-xs">
              {/* Status & Code */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 dark:bg-gray-900/60 p-3 rounded-xl border border-gray-100 dark:border-gray-700">
                  <span className="text-[10px] font-bold uppercase text-gray-400">Current Status</span>
                  <div className="mt-1">
                    <span
                      className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase border ${
                        STATUS_PILL_STYLES[selectedGeneralError.str_status?.toUpperCase()] || STATUS_PILL_STYLES.FAILED
                      }`}
                    >
                      {selectedGeneralError.str_status || "FAILED"}
                    </span>
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-900/60 p-3 rounded-xl border border-gray-100 dark:border-gray-700">
                  <span className="text-[10px] font-bold uppercase text-gray-400">Error Code</span>
                  <p className="font-mono font-bold text-rose-600 dark:text-rose-400 mt-1">
                    {selectedGeneralError.str_error_code || "N/A"}
                  </p>
                </div>
              </div>

              {/* Error Message */}
              <div>
                <span className="text-[10px] font-bold uppercase text-gray-400 block mb-1">
                  Error Reason / Description
                </span>
                <div className="p-3 rounded-xl bg-rose-50/70 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900 text-rose-700 dark:text-rose-300 font-medium leading-relaxed break-words">
                  {selectedGeneralError.str_error_message || "No error reason provided by gateway."}
                </div>
              </div>

              {/* Meta details */}
              <div className="bg-gray-50 dark:bg-gray-900/50 p-3 rounded-xl border border-gray-100 dark:border-gray-700 space-y-1.5 font-mono text-[11px]">
                <div className="flex justify-between">
                  <span className="text-gray-400">Template:</span>
                  <span className="text-gray-800 dark:text-gray-200 font-semibold">{selectedGeneralError.str_template_name || "—"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Msg ID:</span>
                  <span className="text-gray-800 dark:text-gray-200 truncate max-w-[260px]" title={selectedGeneralError.str_operator_message_id}>
                    {selectedGeneralError.str_operator_message_id || "—"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Sent At:</span>
                  <span className="text-gray-800 dark:text-gray-200">
                    {selectedGeneralError.dttm_sent_on ? new Date(selectedGeneralError.dttm_sent_on).toLocaleString() : "—"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Delivered At:</span>
                  <span className="text-gray-800 dark:text-gray-200">
                    {selectedGeneralError.dttm_delivered_on ? new Date(selectedGeneralError.dttm_delivered_on).toLocaleString() : "—"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Read At:</span>
                  <span className="text-gray-800 dark:text-gray-200">
                    {selectedGeneralError.dttm_read_on ? new Date(selectedGeneralError.dttm_read_on).toLocaleString() : "—"}
                  </span>
                </div>
              </div>

              {/* Raw Response */}
              {selectedGeneralError.obj_message_data?.api_response && (
                <div>
                  <span className="text-[10px] font-bold uppercase text-gray-400 block mb-1">
                    Raw Gateway Response
                  </span>
                  <pre className="p-2.5 rounded-xl bg-gray-900 text-gray-200 text-[10px] font-mono overflow-x-auto max-h-36">
                    {typeof selectedGeneralError.obj_message_data.api_response === "object"
                      ? JSON.stringify(selectedGeneralError.obj_message_data.api_response, null, 2)
                      : String(selectedGeneralError.obj_message_data.api_response)}
                  </pre>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-3.5 bg-gray-50 dark:bg-gray-900 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
              <button
                type="button"
                onClick={() => {
                  const errText = `Recipient: +${selectedGeneralError.str_recipient_number}\nStatus: ${selectedGeneralError.str_status}\nError Code: ${selectedGeneralError.str_error_code || 'N/A'}\nMessage: ${selectedGeneralError.str_error_message || 'N/A'}\nMsg ID: ${selectedGeneralError.str_operator_message_id || 'N/A'}`;
                  navigator.clipboard?.writeText(errText);
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-gray-200 dark:border-gray-700 text-xs font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer transition-colors"
              >
                <ClipboardDocumentIcon className="w-3.5 h-3.5" />
                <span>Copy Diagnostics</span>
              </button>
              <button
                type="button"
                onClick={() => setSelectedGeneralError(null)}
                className="px-4 py-1.5 bg-gray-800 hover:bg-gray-900 dark:bg-gray-700 dark:hover:bg-gray-600 text-white font-semibold rounded-xl text-xs cursor-pointer transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* API Campaign Message Error / Diagnostics Modal */}
      {selectedApiError && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs animate-in fade-in duration-150">
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-2xl max-w-lg w-full overflow-hidden">
            {/* Modal Header */}
            <div className="p-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between bg-rose-50/60 dark:bg-rose-950/20">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-rose-100 dark:bg-rose-900/60 flex items-center justify-center text-rose-600 dark:text-rose-400 shrink-0">
                  <ExclamationTriangleIcon className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-gray-900 dark:text-white">
                    API Message Error & Diagnostics
                  </h3>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400 font-mono">
                    Recipient: +{selectedApiError.str_recipient_number}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSelectedApiError(null)}
                className="p-1.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition-colors"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-5 space-y-4 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 dark:bg-gray-900/60 p-3 rounded-xl border border-gray-100 dark:border-gray-700">
                  <span className="text-[10px] font-bold uppercase text-gray-400">Status</span>
                  <div className="mt-1">
                    <span
                      className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase border ${
                        STATUS_PILL_STYLES[selectedApiError.str_status?.toUpperCase()] || STATUS_PILL_STYLES.FAILED
                      }`}
                    >
                      {selectedApiError.str_status || "FAILED"}
                    </span>
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-900/60 p-3 rounded-xl border border-gray-100 dark:border-gray-700">
                  <span className="text-[10px] font-bold uppercase text-gray-400">Template</span>
                  <p className="font-mono font-bold text-gray-900 dark:text-white mt-1">
                    {selectedApiError.str_template_name || "N/A"}
                  </p>
                </div>
              </div>

              {/* Error Message */}
              <div>
                <span className="text-[10px] font-bold uppercase text-gray-400 block mb-1">
                  Error Diagnostic Reason
                </span>
                <div className="p-3 bg-rose-50 dark:bg-rose-950/30 border border-rose-200/80 dark:border-rose-900/50 rounded-xl text-rose-800 dark:text-rose-200 font-mono text-[11px] leading-relaxed break-words">
                  {selectedApiError.str_error_message || "No explicit error payload returned by gateway"}
                </div>
              </div>

              {/* Technical Details Grid */}
              <div className="bg-gray-50 dark:bg-gray-900/60 p-3 rounded-xl border border-gray-100 dark:border-gray-700 space-y-1.5 font-mono text-[11px]">
                <div className="flex justify-between">
                  <span className="text-gray-400">Provider Msg ID:</span>
                  <span className="text-gray-800 dark:text-gray-200 truncate max-w-[260px]">
                    {selectedApiError.str_operator_message_id || "—"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Sender Number:</span>
                  <span className="text-gray-800 dark:text-gray-200">
                    {selectedApiError.str_sender_number ? `+${selectedApiError.str_sender_number}` : "—"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Dispatched At:</span>
                  <span className="text-gray-800 dark:text-gray-200">
                    {selectedApiError.dttm_created_on ? new Date(selectedApiError.dttm_created_on).toLocaleString() : "—"}
                  </span>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-3.5 bg-gray-50 dark:bg-gray-900 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
              <button
                type="button"
                onClick={() => {
                  const errText = `Recipient: +${selectedApiError.str_recipient_number}\nTemplate: ${selectedApiError.str_template_name}\nStatus: ${selectedApiError.str_status}\nError: ${selectedApiError.str_error_message || 'N/A'}\nMsg ID: ${selectedApiError.str_operator_message_id || 'N/A'}`;
                  navigator.clipboard?.writeText(errText);
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-gray-200 dark:border-gray-700 text-xs font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer transition-colors"
              >
                <ClipboardDocumentIcon className="w-3.5 h-3.5" />
                <span>Copy Diagnostics</span>
              </button>
              <button
                type="button"
                onClick={() => setSelectedApiError(null)}
                className="px-4 py-1.5 bg-gray-800 hover:bg-gray-900 dark:bg-gray-700 dark:hover:bg-gray-600 text-white font-semibold rounded-xl text-xs cursor-pointer transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
